import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Input } from "#components/atoms/Input/Input";
import { InputDimensions } from "#components/atoms/InputDimensions/InputDimensions";
import { InputSuffix } from "#components/atoms/InputSuffix/InputSuffix";
import { Field } from "#components/molecules/Field/Field";
import { Modal } from "#components/molecules/Modal/Modal";
import { Select } from "#components/molecules/Select/Select";
import { type CreateMaterialDto } from "#modules/materials/infra/createMaterial.api";
import { useCreateMaterial } from "#modules/materials/view/useCreateMaterial";
import { useMaterialGradeDetails } from "#modules/materials/view/useMaterialGradeDetails";
import {
  AddRawMaterialFormSchema,
  type AddRawMaterialFormValues,
} from "#modules/onboardingV2/domain/addRawMaterialFormSchema";
import { useOnboardingAnalytics } from "#modules/onboardingV2/view/useOnboardingAnalytics";
import { AnalyticsEvent } from "#shared/useAnalytics";
import { getMaterialDimensionsFieldsConfiguration, MaterialTypeSchema } from "shared";

const PRICE_UNIT = "€/format";

type Props = {
  opened: boolean;
  onClose: () => void;
};

const DEFAULT_VALUES: AddRawMaterialFormValues = {
  type: null,
  materialGrade: null,
  name: "",
  dimensions: {},
  price: "",
};

export const AddRawMaterialModal = ({ opened, onClose }: Props) => {
  const { t } = useTranslation();
  const { createMaterial, isPending } = useCreateMaterial();
  const { materialGradeDetails } = useMaterialGradeDetails();
  const { captureOnboardingEvent } = useOnboardingAnalytics("rawMaterialSpecification");

  const { control, handleSubmit, reset } = useForm<
    AddRawMaterialFormValues,
    unknown,
    CreateMaterialDto
  >({
    defaultValues: DEFAULT_VALUES,
    resolver: zodResolver(AddRawMaterialFormSchema),
  });

  const type = useWatch({ control, name: "type" });
  const dimensionKeys = getMaterialDimensionsFieldsConfiguration(type);

  const typeOptions = MaterialTypeSchema.options.map((option) => ({
    value: option,
    label: t(`qrl.materialTypes.${option}`),
  }));

  const materialGradeOptions = materialGradeDetails.map(
    ({ materialGrade: grade, name: label }) => ({
      value: grade,
      label,
    }),
  );

  const handleClose = () => {
    reset(DEFAULT_VALUES);
    onClose();
  };

  const onSubmit = (data: CreateMaterialDto) => {
    createMaterial(data, {
      onSuccess: () => {
        captureOnboardingEvent(AnalyticsEvent.OnboardingMaterialAdded, {
          materialType: data.type,
        });
        handleClose();
      },
    });
  };

  return (
    <Modal
      opened={opened}
      onClose={handleClose}
      title={t("onboardingV2.rawMaterialSpecification.addModal.title")}
      closeText={t("common.cancel")}
      primaryText={t("common.add")}
      onPrimary={handleSubmit(onSubmit)}
      primaryLoading={isPending}
    >
      <Controller
        name="type"
        control={control}
        render={({ field, fieldState }) => (
          <Field
            label={t("onboardingV2.rawMaterialSpecification.addModal.formatLabel")}
            error={fieldState.error ? t("common.requiredField") : undefined}
          >
            <Select
              value={field.value}
              onChange={field.onChange}
              data={typeOptions}
              placeholder={t("onboardingV2.rawMaterialSpecification.addModal.formatPlaceholder")}
              error={Boolean(fieldState.error)}
              aria-label={t("onboardingV2.rawMaterialSpecification.addModal.formatLabel")}
            />
          </Field>
        )}
      />
      <Controller
        name="materialGrade"
        control={control}
        render={({ field, fieldState }) => (
          <Field
            label={t("qrl.materialGrade")}
            error={fieldState.error ? t("common.requiredField") : undefined}
          >
            <Select
              value={field.value}
              onChange={field.onChange}
              data={materialGradeOptions}
              placeholder={t(
                "onboardingV2.rawMaterialSpecification.addModal.materialGradePlaceholder",
              )}
              error={Boolean(fieldState.error)}
              aria-label={t("qrl.materialGrade")}
            />
          </Field>
        )}
      />
      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }) => (
          <Field
            label={t("qrl.materialSpecification")}
            error={fieldState.error ? t("common.requiredField") : undefined}
          >
            <Input
              value={field.value}
              onChange={field.onChange}
              placeholder={t("onboardingV2.rawMaterialSpecification.addModal.namePlaceholder")}
              error={Boolean(fieldState.error)}
              aria-label={t("qrl.materialSpecification")}
            />
          </Field>
        )}
      />
      <Controller
        name="dimensions"
        control={control}
        render={({ field, fieldState }) => (
          <Field
            label={t("onboardingV2.rawMaterialSpecification.columns.dimensions")}
            error={fieldState.error ? t("common.requiredField") : undefined}
          >
            <InputDimensions
              dimensions={dimensionKeys.map(({ key }) => key)}
              thickness={field.value.thickness}
              diameter={field.value.diameter}
              height={field.value.height}
              width={field.value.width}
              length={field.value.length}
              onThicknessChange={(value) => field.onChange({ ...field.value, thickness: value })}
              onDiameterChange={(value) => field.onChange({ ...field.value, diameter: value })}
              onHeightChange={(value) => field.onChange({ ...field.value, height: value })}
              onWidthChange={(value) => field.onChange({ ...field.value, width: value })}
              onLengthChange={(value) => field.onChange({ ...field.value, length: value })}
              error={Boolean(fieldState.error)}
            />
          </Field>
        )}
      />
      <Controller
        name="price"
        control={control}
        render={({ field, fieldState }) => (
          <Field
            label={t("common.price")}
            error={fieldState.error ? t("common.requiredField") : undefined}
          >
            <InputSuffix
              value={field.value}
              onChange={field.onChange}
              placeholder="0,00"
              suffix={PRICE_UNIT}
              error={Boolean(fieldState.error)}
              aria-label={t("common.price")}
            />
          </Field>
        )}
      />
    </Modal>
  );
};
